/* =====================================================================
 *  School System — HUB (Choose a role or enter parent portal via Student ID)
 * ===================================================================== */
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { schoolSystemApi } from "@/lib/schoolSystemApi";
import {
  UserCircle, Chalkboard, Buildings, ArrowLeft, IdentificationCard,
} from "@phosphor-icons/react";

export default function SchoolSystem() {
  const nav = useNavigate();
  const [roles, setRoles] = useState(null);
  const [studentId, setStudentId] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [teacherPickerOpen, setTeacherPickerOpen] = useState(false);

  useEffect(() => {
    schoolSystemApi.myRoles().then(setRoles).catch(() => setRoles({ roles: [] }));
  }, []);

  const openParent = (e) => {
    e.preventDefault();
    if (!studentId.trim()) return toast.error("Enter your Student ID");
    const email = parentEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return toast.error("Enter your email address so the school can reach you");
    }
    const qp = new URLSearchParams({ parent_email: email });
    nav(`/school-system/parent/${studentId.trim()}?${qp.toString()}`);
  };

  // Teacher role click: single assignment → go straight in; multiple →
  // open the picker so the teacher chooses which class to work on.
  const openTeacher = () => {
    const teacherRoles = (roles?.roles || []).filter(r => r.role === "teacher");
    if (teacherRoles.length === 0) {
      return toast.info("Ask the headteacher to assign you to a grade first.");
    }
    if (teacherRoles.length === 1) {
      const t = teacherRoles[0];
      return nav(`/school-system/${t.school_id}/teacher?grade=${encodeURIComponent(t.grade)}`);
    }
    setTeacherPickerOpen(true);
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6" data-testid="school-system-hub">
      <div className="flex items-center gap-3">
        <Link to="/schools" className="text-white/60 hover:text-white text-sm flex items-center gap-1">
          <ArrowLeft size={14} weight="bold" /> Find School
        </Link>
        <span className="text-white/30">/</span>
        <span className="text-white/80 text-sm">School System</span>
      </div>
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-black flex items-center gap-2">
          <Chalkboard size={34} weight="fill" className="text-indigo-400" /> School System
        </h1>
        <p className="text-white/60 text-sm md:text-base mt-1">
          Secure portal for Parents, Teachers, and Headteachers / Principals — Creche through University.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <RoleTile
          testId="role-parent"
          icon={UserCircle}
          color="text-sky-400"
          title="Parent"
          desc="Access your child's records, report card, homework and fees using only their Student ID."
          onClick={() => document.getElementById("parent-form")?.scrollIntoView({ behavior: "smooth" })}
        />
        <RoleTile
          testId="role-teacher"
          icon={Chalkboard}
          color="text-amber-400"
          title="Teacher"
          desc="Manage your assigned grade's students, subjects, records, attendance, homework and lesson notes."
          onClick={openTeacher}
        />
        <RoleTile
          testId="role-headteacher"
          icon={Buildings}
          color="text-emerald-400"
          title="Headteacher / Principal"
          desc="Manage teachers, students, subjects, approvals, parent messages and appointments."
          onClick={() => {
            const ht = roles?.roles?.find((r) => r.role === "headteacher");
            if (!ht) return toast.info("You are not registered as a headteacher yet.");
            nav(`/school-system/${ht.school_id}/headteacher`);
          }}
        />
      </div>

      {/* Parent quick-entry */}
      <Card id="parent-form" className="bg-[#121212] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <IdentificationCard size={22} weight="fill" className="text-sky-400" /> Parent access — enter Student ID
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={openParent} className="space-y-2">
            <div className="flex gap-2 flex-wrap">
              <Input
                data-testid="parent-student-id"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value.toUpperCase())}
                placeholder="Student ID (e.g. TAIN-2026-000001)"
                className="flex-1 min-w-[220px] bg-white/5 border-white/10"
              />
              <Input
                data-testid="parent-email-input"
                type="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                placeholder="Your email address"
                className="flex-1 min-w-[220px] bg-white/5 border-white/10"
              />
              <Button data-testid="parent-open-btn" type="submit" className="bg-sky-600 hover:bg-sky-700">
                Open portal
              </Button>
            </div>
            <p className="text-xs text-white/50">
              Enter both your child's Student ID and your email so the school can
              reach you with results, fees and important announcements.
            </p>
          </form>
        </CardContent>
      </Card>

      {(roles?.roles?.length || 0) > 0 && (
        <Card className="bg-white/[0.02] border-white/10">
          <CardHeader><CardTitle className="font-heading text-lg">Your school access</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {roles.roles.map((r, i) => (
              <div key={i} className="flex items-center justify-between p-2 rounded-md bg-white/[0.03]" data-testid={`my-role-${i}`}>
                <div>
                  <p className="font-heading font-bold">{r.school_name}</p>
                  <p className="text-xs text-white/60">
                    {r.role === "headteacher" ? "Headteacher" : `Teacher — ${r.grade}`}
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="border-white/20 bg-transparent hover:bg-white/5"
                  onClick={() => nav(r.role === "headteacher"
                    ? `/school-system/${r.school_id}/headteacher`
                    : `/school-system/${r.school_id}/teacher?grade=${encodeURIComponent(r.grade)}`)}
                >
                  Open portal
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {teacherPickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
              data-testid="teacher-grade-picker"
              onClick={() => setTeacherPickerOpen(false)}>
          <div className="bg-[#0d0d0d] border border-white/10 rounded-xl p-5 max-w-sm w-full space-y-3"
                onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-xl font-black">Which class do you want to work on?</h3>
            <p className="text-white/50 text-xs">
              You are assigned to more than one grade. Pick one to enter its portal.
            </p>
            <div className="space-y-2 max-h-[65vh] overflow-y-auto pr-1">
              {(roles?.roles || []).filter(r => r.role === "teacher").map((t, i) => (
                <button key={`${t.school_id}-${t.grade}-${i}`}
                        data-testid={`teacher-grade-option-${i}`}
                        onClick={() => {
                          setTeacherPickerOpen(false);
                          nav(`/school-system/${t.school_id}/teacher?grade=${encodeURIComponent(t.grade)}`);
                        }}
                        className="w-full flex items-center justify-between p-3 rounded-md
                                   bg-white/[0.03] hover:bg-white/[0.08] border border-white/10
                                   text-left">
                  <div>
                    <p className="font-heading font-bold">{t.grade}</p>
                    <p className="text-xs text-white/50">{t.school_name || t.school_id}</p>
                  </div>
                  <span className="text-white/40 text-lg">→</span>
                </button>
              ))}
            </div>
            <div className="flex justify-end">
              <Button variant="outline"
                       onClick={() => setTeacherPickerOpen(false)}
                       className="border-white/20 bg-transparent">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RoleTile({ testId, icon: Icon, color, title, desc, onClick }) {
  return (
    <Card
      data-testid={testId}
      onClick={onClick}
      className="bg-[#121212] border-white/10 hover:border-white/30 cursor-pointer transition-colors"
    >
      <CardContent className="p-5 space-y-2">
        <Icon size={36} weight="fill" className={color} />
        <h3 className="font-heading text-xl font-black">{title}</h3>
        <p className="text-sm text-white/70">{desc}</p>
      </CardContent>
    </Card>
  );
}
